/**
 * sessionStorage key prefix for Optimizely CMAB prediction entries.
 * @category Utils
 */
export const OPTIMIZELY_CMAB_CACHE_PREFIX = 'optly_cmab:'

/**
 * How long a cached CMAB prediction stays valid.
 * @category Utils
 */
export const OPTIMIZELY_CMAB_CACHE_TTL_MS = 30 * 60 * 1000

/**
 * Cap on namespaced CMAB cache entries. Oldest entries are evicted first.
 * @category Utils
 */
export const OPTIMIZELY_CMAB_CACHE_MAX_ENTRIES = 100

/**
 * Options for {@link createOptimizelySessionStorageCmabCache}.
 * @category Utils
 */
export type OptimizelyCmabCacheOptions = {
  prefix?: string
  ttlMs?: number
  maxEntries?: number
}

type StoredEntry = {
  v: string
  t: number
  e: number
}

const getSessionStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  }
  catch {
    return null
  }
}

const ownKeys = (storage: Storage, prefix: string) => {
  const keys: string[] = []
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key?.startsWith(prefix)) keys.push(key)
  }
  return keys
}

const evictOldest = (storage: Storage, prefix: string, count = 1) => {
  const entries = ownKeys(storage, prefix).map((key) => {
    try {
      return { key, t: (JSON.parse(storage.getItem(key) || '') as StoredEntry).t || 0 }
    }
    catch {
      return { key, t: 0 }
    }
  })
  entries.sort((a, b) => a.t - b.t)
  entries.slice(0, count).forEach(({ key }) => storage.removeItem(key))
}

const isStoredEntry = (value: unknown): value is StoredEntry =>
  typeof value === 'object'
  && value !== null
  && typeof (value as StoredEntry).e === 'number'
  && 'v' in value

/**
 * Builds a sync Optimizely CMAB cache (`CacheWithRemove<string>`) backed by
 * `sessionStorage`. Falls back to an in-memory Map when storage is unavailable.
 * Passed to `createInstance({ cmab: { cache } })` so `decideAsync` can reuse
 * CMAB predictions within the tab for `ttlMs`.
 * @param options - Optional cache tuning.
 * @param options.prefix - sessionStorage key namespace.
 * @param options.ttlMs - Max age of an entry in milliseconds.
 * @param options.maxEntries - Cap on stored entries; oldest are evicted first.
 * @returns A sync cache with `save` / `lookup` / `remove` / `reset`.
 * @category Utils
 */
export const createOptimizelySessionStorageCmabCache = ({
  prefix = OPTIMIZELY_CMAB_CACHE_PREFIX,
  ttlMs = OPTIMIZELY_CMAB_CACHE_TTL_MS,
  maxEntries = OPTIMIZELY_CMAB_CACHE_MAX_ENTRIES,
}: OptimizelyCmabCacheOptions = {}) => {
  const fallback = new Map<string, string>()

  const save = (key: string, value: string) => {
    const storage = getSessionStorage()
    if (!storage) {
      fallback.set(key, value)
      return
    }

    const now = Date.now()
    const payload = JSON.stringify({ v: value, t: now, e: now + ttlMs })
    const storageKey = prefix + key

    try {
      const overflow = ownKeys(storage, prefix).length - maxEntries + 1
      if (overflow > 0 && storage.getItem(storageKey) === null) {
        evictOldest(storage, prefix, overflow)
      }
      storage.setItem(storageKey, payload)
    }
    catch {
      try {
        evictOldest(storage, prefix, 5)
        storage.setItem(storageKey, payload)
      }
      catch {
        /* quota still exceeded — skip caching */
      }
    }
  }

  const lookup = (key: string): string | undefined => {
    const storage = getSessionStorage()
    if (!storage) return fallback.get(key)

    const storageKey = prefix + key
    try {
      const raw = storage.getItem(storageKey)
      if (raw === null) return undefined

      const entry = JSON.parse(raw)
      if (!isStoredEntry(entry)) {
        storage.removeItem(storageKey)
        return undefined
      }
      if (entry.e <= Date.now()) {
        storage.removeItem(storageKey)
        return undefined
      }
      return entry.v
    }
    catch {
      try {
        storage.removeItem(storageKey)
      }
      catch {
        /* ignore */
      }
      return undefined
    }
  }

  const remove = (key: string) => {
    const storage = getSessionStorage()
    if (!storage) return fallback.delete(key)

    try {
      const storageKey = prefix + key
      const existed = storage.getItem(storageKey) !== null
      storage.removeItem(storageKey)
      return existed
    }
    catch {
      return false
    }
  }

  const reset = () => {
    fallback.clear()
    const storage = getSessionStorage()
    if (!storage) return

    try {
      ownKeys(storage, prefix).forEach((key) => storage.removeItem(key))
    }
    catch {
      /* ignore */
    }
  }

  return {
    operation: 'sync' as const,
    save,
    lookup,
    remove,
    reset,
  }
}

/**
 * Default CMAB cache used by the Nuxt Optimizely plugin.
 * @category Utils
 */
export const optimizelySessionStorageCmabCache = createOptimizelySessionStorageCmabCache()
